import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import { appendFilterClause } from '../build-filter-by';
import {
  DEFAULT_SEARCH_ITEMS_PER_PAGE,
  HOME_SEARCH_INDEX_ID,
  MAIN_SEARCH_INDEX_ID,
  TYPESENSE_PUBLISHED_PRODUCTS_FILTER,
} from '../constants';
import {
  getTypesenseServerConfig,
  MAIN_SEARCH_ADDITIONAL_PARAMETERS,
  NUMERIC_ONLY_MAIN_SEARCH_PARAMETERS,
  TYPESENSE_COLLECTION,
  normalizeTypesenseSort,
} from '../typesense-search-config';
import { wrapSearchClientWithNumericRouting } from '../search-client-numeric-routing';

type TFields = {
  filters?: string;
  ordering?: string;
  items_per_page?: number;
};

const server = getTypesenseServerConfig();

function remapIndexName(indexName: string): string {
  const frontendIds = [MAIN_SEARCH_INDEX_ID, HOME_SEARCH_INDEX_ID];
  for (const id of frontendIds) {
    if (indexName === id) return TYPESENSE_COLLECTION;
    const sortPrefix = `${id}/sort/`;
    if (indexName.startsWith(sortPrefix)) {
      const sortBy = indexName.slice(sortPrefix.length);
      const normalized = normalizeTypesenseSort(sortBy) ?? sortBy;
      return `${TYPESENSE_COLLECTION}/sort/${normalized}`;
    }
    if (indexName.startsWith(id + '/'))
      return TYPESENSE_COLLECTION + indexName.slice(id.length);
  }
  return indexName;
}

function withCollectionRemap(baseClient: any): any {
  return {
    ...baseClient,
    search: (requests: any[]) => {
      const remapped = requests.map((req) => ({
        ...req,
        indexName: remapIndexName(req.indexName),
      }));
      return baseClient.search(remapped);
    },
  };
}

/**
 * Dynamic section searches (home carousels, product widgets, etc.) never
 * originate from the main header search box, so analytics must always be
 * disabled. `enable_analytics: false` is set at adapter level — the only
 * place TypesenseInstantSearchAdapter reads it.
 */
const createPairedSearchClients = ({
  filters,
  ordering,
  items_per_page,
}: TFields) => {
  const mergedFilter = appendFilterClause(
    TYPESENSE_PUBLISHED_PRODUCTS_FILTER,
    filters,
  );
  const perPage = items_per_page || DEFAULT_SEARCH_ITEMS_PER_PAGE;
  const sortBy = normalizeTypesenseSort(ordering);

  const baseAdditional = {
    filter_by: mergedFilter,
    ...(sortBy ? { sort_by: sortBy } : {}),
    per_page: perPage,
    enable_analytics: false,
  };

  const mainAdapter = new TypesenseInstantSearchAdapter({
    server,
    additionalSearchParameters: {
      ...MAIN_SEARCH_ADDITIONAL_PARAMETERS,
      ...baseAdditional,
    } as Record<string, unknown>,
  });

  const numericAdapter = new TypesenseInstantSearchAdapter({
    server,
    additionalSearchParameters: {
      ...NUMERIC_ONLY_MAIN_SEARCH_PARAMETERS,
      ...baseAdditional,
    } as Record<string, unknown>,
  });

  return wrapSearchClientWithNumericRouting(
    mainAdapter.searchClient,
    numericAdapter.searchClient,
  );
};

export const getDynamicProductSearchClient = (props: TFields) => {
  const routedClient = createPairedSearchClients(props);
  return withCollectionRemap(routedClient);
};
